import { createFileRoute, redirect, Outlet } from "@tanstack/react-router";
import { isSignedOutError } from "@/lib/queryClient";
import { meQueryOptionsFor, useMe } from "@/hooks/useCloudChat";
import { useEnsureAgent } from "@/hooks/ensureAgent";
import { stripLegacyActAsSearch } from "@/lib/legacyActing";
import { ChatProvider, useReattachRuns } from "@/chat/ChatProvider";
import { webChatConfig } from "@/chat-config";
import { setupRedirect } from "./authGate";

function ReattachRuns() {
  // Resume any web run still in flight from a previous page load (per-run /web/runs
  // streams; no app-level connection — each run owns its own stream).
  useReattachRuns();
  return null;
}

function AgentsLayout() {
  const { data: me } = useMe();
  const orgId = me?.organization?.id ?? "";
  // Onboarding is gone — the app itself seeds the org's agent (with the browser
  // timezone) the first time the user lands here, silently and once per org.
  useEnsureAgent();
  // The ChatProvider spans the chats home ("/chats") and conversations ("/chats/$id"), so the
  // in-flight overlay (and its stream) survives navigation between them.
  //
  // Shared navigation lives in the parent authenticated layout so moving to Administration
  // does not replace the sidebar or profile menu. Workspace chat remains scoped to this branch.
  return (
    <ChatProvider config={webChatConfig(orgId)}>
      <ReattachRuns />
      <Outlet />
    </ChatProvider>
  );
}

export const Route = createFileRoute("/_auth/_agents")({
  beforeLoad: async ({ context, location, preload }) => {
    // Convert apiClient's SessionExpiredError (thrown by any downstream
    // 401) into a clean /signin redirect — otherwise the error bubbles
    // to the root ErrorBoundary and the user sees an error page instead
    // of a sign-in prompt. Router redirects pass through the catch and
    // re-throw by `instanceof` miss.
    try {
      // Org membership required — user without an org lands on the invite wall.
      // `preload` fetches speculatively so hovering a child link (this beforeLoad
      // runs on preload of the whole branch) can't sign the user out.
      const me = await context.queryClient.ensureQueryData(meQueryOptionsFor(preload));
      // Old bookmarks may carry the retired act-as params (`?act_as_org=&act_as_name=`). They are
      // never honored: the URL keeps only the resource (the study id decides access). Those links
      // came from the admin board, so staff land on the same study in Admin; anyone else stays put.
      const legacy = stripLegacyActAsSearch(location.search as Record<string, unknown>);
      if (legacy && !preload) {
        const study = /^\/studies\/([0-9a-f-]{36})\/?$/i.exec(location.pathname)?.[1];
        if (study && me.is_platform_admin)
          throw redirect({
            to: "/admin/studies/$id",
            params: { id: study },
            search: legacy,
            replace: true,
          });
        throw redirect({ to: location.pathname, search: legacy, replace: true });
      }
      // Org-less users hit the invite wall; everyone else lands straight in. There is no
      // onboarding gate — the wizard was removed and the agent is seeded on arrival
      // (see useEnsureAgent). `onboarding_completed` in user_metadata is now vestigial.
      const dest = setupRedirect({ staff: me.is_platform_admin, hasOrg: me.organization != null });
      if (dest) throw redirect({ to: dest });
    } catch (e) {
      if (isSignedOutError(e)) {
        // A speculative (hover-preload) failure left the session intact — don't
        // redirect. Re-throw so the router just aborts this best-effort preload;
        // the real click re-runs beforeLoad and redirects authoritatively.
        if ((e as { speculative?: boolean }).speculative) throw e;
        throw redirect({ to: "/signin", search: { redirect: location.href } });
      }
      throw e;
    }
  },
  component: AgentsLayout,
});
